import { Metadata } from "next";
import { Post, Category, Tag } from "@/types";
import { get } from "lodash";
import { generateDetailPath } from "@/lib/utils";

export const SITE_CONFIG = {
  name: "biblevod.com",
  description:
    "Zests is your vibrant community for real conversations, shared experiences, and genuine connection.",
  url: "https://biblevod.com",
  logo: "https://biblevod.com/images/logo.png",
  ogImage: "https://biblevod.com/images/logo.png",
  creator: "biblevod.com",
  slogan: "Find your community, share your world",
  twitterHandle: "@zests_ai",
  facebookAppId: "",
  locale: "en_US",
  alternateLocales: ["zh_CN"],
};

export function generateHomeMetadata(): Metadata {
  return {
    title: `${SITE_CONFIG.name} | ${SITE_CONFIG.slogan}`,
    description: SITE_CONFIG.description,
    authors: [{ name: SITE_CONFIG.creator }],
    creator: SITE_CONFIG.creator,
    publisher: SITE_CONFIG.name,
    openGraph: {
      type: "website",
      url: SITE_CONFIG.url,
      title: `${SITE_CONFIG.name} | ${SITE_CONFIG.slogan}`,
      description: SITE_CONFIG.description,
      siteName: SITE_CONFIG.name,
      locale: SITE_CONFIG.locale,
      images: [
        {
          url: SITE_CONFIG.ogImage,
          width: 1200,
          height: 630,
          alt: `${SITE_CONFIG.name} homepage`,
          type: "image/png",
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      site: SITE_CONFIG.twitterHandle,
      creator: SITE_CONFIG.twitterHandle,
      title: `${SITE_CONFIG.name} | ${SITE_CONFIG.slogan}`,
      description: SITE_CONFIG.description,
      images: [SITE_CONFIG.ogImage],
    },
    alternates: {
      canonical: SITE_CONFIG.url,
      languages: {
        "en-US": SITE_CONFIG.url,
        "zh-CN": `${SITE_CONFIG.url}/zh`,
      },
    },
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
    verification: {
      google: "your-google-verification-code",
      yandex: "your-yandex-verification-code",
    },
    appleWebApp: {
      capable: true,
      title: SITE_CONFIG.name,
      statusBarStyle: "default",
    },
    formatDetection: {
      telephone: false,
    },
  };
}

export function generatePostMetadata(post: Post): Metadata {
  const title = `${get(post, "title", "")} | ${get(post, "author.name", "")} | ${SITE_CONFIG.name}`;
  const description = get(post, "summary", "");

  // 使用规范化的URL路径
  const canonicalSlug = generateDetailPath(
    post.docid || post.doc_id,
    post.title,
  );
  const url = `${SITE_CONFIG.url}/posts/${canonicalSlug}`;
  const imageUrl = get(post, "images[0]", SITE_CONFIG.ogImage);
  const tags = (get(post, "tags", []) || []).filter(Boolean);
  const category = get(post, "category.name", "");
  const authorName = get(post, "author.name", "");
  const readingTime = estimateReadingTime(
    get(post, "content", "") || get(post, "summary", ""),
  );

  return {
    title,
    description,
    authors: [{ name: authorName }],
    creator: authorName,
    publisher: SITE_CONFIG.name,
    category,
    openGraph: {
      type: "article",
      url,
      title,
      description,
      siteName: SITE_CONFIG.name,
      locale: SITE_CONFIG.locale,
      images: [
        {
          url: imageUrl,
          width: 1200,
          height: 630,
          alt: post.title,
          type: "image/jpeg",
        },
      ],
      publishedTime: post.publishedAt,
      modifiedTime: post.updatedAt,
      expirationTime: undefined,
      authors: [authorName],
      section: category,
      tags: tags.map((tag: any) => tag.name),
    },
    twitter: {
      card: "summary_large_image",
      site: SITE_CONFIG.twitterHandle,
      creator: SITE_CONFIG.twitterHandle,
      title,
      description,
      images: [imageUrl],
    },
    alternates: {
      canonical: url,
    },
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
    other: {
      "article:reading_time": readingTime.toString(),
      "article:word_count": estimateWordCount(
        get(post, "content", "") || get(post, "summary", ""),
      ).toString(),
    },
  };
}

function estimateReadingTime(content: string): number {
  const wordsPerMinute = 200;
  const wordCount = estimateWordCount(content);
  return Math.ceil(wordCount / wordsPerMinute);
}

function estimateWordCount(content: string): number {
  const textContent = content.replace(/<[^>]*>/g, "");
  const chineseChars = (textContent.match(/[\u4e00-\u9fa5]/g) || []).length;
  const englishWords = textContent
    .replace(/[\u4e00-\u9fa5]/g, "")
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
  return chineseChars + englishWords;
}

export function generateCategoryMetadata(
  category: Category,
  page: number = 1,
  hasMore: boolean = false,
  hasPrevious: boolean = false,
  hasValidData: boolean = true,
): Metadata {
  const title =
    page === 1
      ? `Category: ${category.name} | ${SITE_CONFIG.name}`
      : `Category: ${category.name} - Page ${page} | ${SITE_CONFIG.name}`;

  const description =
    page === 1
      ? `In biblevod.com's "${category.name}" category, browse the latest and most popular ${category.name} themed articles.`
      : `In biblevod.com's "${category.name}" category, browse the latest and most popular ${category.name} themed articles. Page ${page} content display.`;

  const url =
    page === 1
      ? `${SITE_CONFIG.url}/category/${category.slug}`
      : `${SITE_CONFIG.url}/category/${category.slug}?page=${page}`;

  // 构建分页链接
  const nextPageUrl = hasMore
    ? `${SITE_CONFIG.url}/category/${category.slug}?page=${page + 1}`
    : undefined;

  const prevPageUrl =
    hasPrevious && page > 1
      ? page === 2
        ? `${SITE_CONFIG.url}/category/${category.slug}` // 第1页不需要page参数
        : `${SITE_CONFIG.url}/category/${category.slug}?page=${page - 1}`
      : undefined;

  // 构建alternates对象
  const alternates: any = {
    canonical: url,
  };

  // 添加分页链接
  if (nextPageUrl) {
    alternates.next = nextPageUrl;
  }
  if (prevPageUrl) {
    alternates.prev = prevPageUrl;
  }

  return {
    title,
    description,
    openGraph: {
      type: "website",
      url,
      title,
      description,
      images: [
        {
          url: SITE_CONFIG.ogImage,
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [SITE_CONFIG.ogImage],
    },
    alternates,
    robots: {
      index: hasValidData, // 有数据时允许索引，无数据时noindex
      follow: true,
      noarchive: page > 1, // 分页页面不存档
      nosnippet: !hasValidData || page > 3, // 无数据或第3页之后不显示摘要
    },
    other: {
      "pagination-page": page.toString(),
      "pagination-pages": hasMore ? "unknown" : page.toString(),
    },
  };
}

export function generateTagMetadata(
  tag: Tag,
  page: number = 1,
  hasMore: boolean = false,
  hasPrevious: boolean = false,
  hasValidData: boolean = true,
): Metadata {
  const title =
    page === 1
      ? `Tag: ${tag.name} | ${SITE_CONFIG.name}`
      : `Tag: ${tag.name} - Page ${page} | ${SITE_CONFIG.name}`;

  const description =
    page === 1
      ? `In biblevod.com's "${tag.name}" tag, discover the latest related articles and shares.`
      : `In biblevod.com's "${tag.name}" tag, discover the latest related articles and shares. Page ${page} content display.`;

  const url =
    page === 1
      ? `${SITE_CONFIG.url}/tag/${tag.slug}`
      : `${SITE_CONFIG.url}/tag/${tag.slug}?page=${page}`;

  // 构建分页链接
  const nextPageUrl = hasMore
    ? `${SITE_CONFIG.url}/tag/${tag.slug}?page=${page + 1}`
    : undefined;

  const prevPageUrl =
    hasPrevious && page > 1
      ? page === 2
        ? `${SITE_CONFIG.url}/tag/${tag.slug}` // 第1页不需要page参数
        : `${SITE_CONFIG.url}/tag/${tag.slug}?page=${page - 1}`
      : undefined;

  // 构建alternates对象
  const alternates: any = {
    canonical: url,
  };

  // 添加分页链接
  if (nextPageUrl) {
    alternates.next = nextPageUrl;
  }
  if (prevPageUrl) {
    alternates.prev = prevPageUrl;
  }

  return {
    title,
    description,
    openGraph: {
      type: "website",
      url,
      title,
      description,
      images: [
        {
          url: SITE_CONFIG.ogImage,
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [SITE_CONFIG.ogImage],
    },
    alternates,
    robots: {
      index: hasValidData, // 有数据时允许索引，无数据时noindex
      follow: true,
      noarchive: page > 1, // 分页页面不存档
      nosnippet: !hasValidData, // 无数据时不显示摘要
    },
    other: {
      "pagination-page": page.toString(),
      "pagination-pages": hasMore ? "unknown" : page.toString(),
    },
  };
}

export function generateStructuredData(post: Post) {
  const canonicalSlug = generateDetailPath(
    post.docid || post.doc_id,
    post.title,
  );
  const url = `${SITE_CONFIG.url}/posts/${canonicalSlug}`;
  const imageUrl = get(post, "images[0]", SITE_CONFIG.ogImage);
  const authorName = get(post, "author.username", "");
  const readingTime = estimateReadingTime(
    get(post, "content", "") || get(post, "summary", ""),
  );
  const wordCount = estimateWordCount(
    get(post, "content", "") || get(post, "summary", ""),
  );

  // 处理评论数据
  const qualityComments = (post.qualityComments || []).map((comment: any) => ({
    "@type": "Comment",
    author: {
      "@type": "Person",
      name: comment.nickname || "Anonymous",
      url: comment.author?.url || undefined,
    },
    datePublished: comment.createAt,
    text: comment.comment,
    url: url,
    upvoteCount: comment.like,
  }));

  // 计算评论总数
  const commentCount = post.comment_count;

  const structuredData: any = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": url,
    headline: post.title,
    alternativeHeadline: post.summary,
    author: {
      "@type": "Person",
      name: authorName,
      // url: `${SITE_CONFIG.url}/users/${get(post, "author.username", "")}`,
      image: {
        "@type": "ImageObject",
        url: get(post, "author.avatar", ""),
      },
    },
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    publisher: {
      "@type": "Organization",
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_CONFIG.url}/images/logo.png`,
        width: 200,
        height: 200,
      },
    },
    description: post.summary,
    articleBody: get(post, "content", ""),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    image: {
      "@type": "ImageObject",
      url: imageUrl,
      width: 1200,
      height: 630,
    },
    articleSection: get(post, "category.name", ""),
    wordCount,
    timeRequired: `PT${readingTime}M`,
    inLanguage: SITE_CONFIG.locale,
    isAccessibleForFree: true,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
    },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", ".post-content"],
    },
  };

  // 添加评论相关数据
  if (commentCount > 0) {
    structuredData.commentCount = commentCount;
  }

  if (qualityComments.length > 0) {
    structuredData.comment = qualityComments;
  }

  // 添加交互统计
  if (post.likes || commentCount > 0) {
    structuredData.interactionStatistic = [];

    if (post.likes) {
      structuredData.interactionStatistic.push({
        "@type": "InteractionCounter",
        interactionType: "https://schema.org/LikeAction",
        userInteractionCount: post.likes,
      });
    }

    if (commentCount > 0) {
      structuredData.interactionStatistic.push({
        "@type": "InteractionCounter",
        interactionType: "https://schema.org/CommentAction",
        userInteractionCount: commentCount,
      });
    }
  }

  return structuredData;
}

export function generateWebSiteStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_CONFIG.url}/#website`,
    name: SITE_CONFIG.name,
    alternateName: SITE_CONFIG.slogan,
    url: SITE_CONFIG.url,
    description: SITE_CONFIG.description,
    inLanguage: SITE_CONFIG.locale,
    copyrightYear: new Date().getFullYear(),
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_CONFIG.url}/#organization`,
    },
    potentialAction: [
      {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_CONFIG.url}/search?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    ],
  };
}

export function generateOrganizationStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_CONFIG.url}/#organization`,
    name: SITE_CONFIG.name,
    alternateName: SITE_CONFIG.slogan,
    url: SITE_CONFIG.url,
    logo: {
      "@type": "ImageObject",
      url: SITE_CONFIG.logo,
      width: 200,
      height: 200,
    },
    description: SITE_CONFIG.description,
    foundingDate: "2024",
    sameAs: [
      `https://twitter.com/${SITE_CONFIG.twitterHandle.replace("@", "")}`,
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      url: `${SITE_CONFIG.url}/contact`,
    },
  };
}

export function generateBreadcrumbStructuredData(
  items: Array<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// 新增：为explore页面生成分页SEO
export function generateExploreMetadata(
  page: number = 1,
  hasMore: boolean = false,
  hasPrevious: boolean = false,
): Metadata {
  const title =
    page === 1
      ? `Explore | ${SITE_CONFIG.name}`
      : `Explore - Page ${page} | ${SITE_CONFIG.name}`;

  const description =
    page === 1
      ? `Explore the latest and most popular posts on ${SITE_CONFIG.name}. Discover trending content and join the conversation.`
      : `Explore the latest and most popular posts on ${SITE_CONFIG.name}. Page ${page} content display.`;

  const url =
    page === 1
      ? `${SITE_CONFIG.url}/explore`
      : `${SITE_CONFIG.url}/explore?page=${page}`;

  // 构建分页链接
  const nextPageUrl = hasMore
    ? `${SITE_CONFIG.url}/explore?page=${page + 1}`
    : undefined;

  const prevPageUrl =
    hasPrevious && page > 1
      ? page === 2
        ? `${SITE_CONFIG.url}/explore` // 第1页不需要page参数
        : `${SITE_CONFIG.url}/explore?page=${page - 1}`
      : undefined;

  // 构建alternates对象
  const alternates: any = {
    canonical: url,
  };

  // 添加分页链接
  if (nextPageUrl) {
    alternates.next = nextPageUrl;
  }
  if (prevPageUrl) {
    alternates.prev = prevPageUrl;
  }

  return {
    title,
    description,
    openGraph: {
      type: "website",
      url,
      title,
      description,
      images: [
        {
          url: SITE_CONFIG.ogImage,
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [SITE_CONFIG.ogImage],
    },
    alternates,
    robots: {
      index: hasMore,
      follow: true,
      noarchive: page > 1, // 分页页面不存档
      nosnippet: page > 3, // 第3页之后不显示摘要
    },
    other: {
      "pagination-page": page.toString(),
      "pagination-pages": hasMore ? "unknown" : page.toString(),
    },
  };
}
